import moment from 'moment';
import localization from 'assets/localization/localization';

/** Matches backend CommonHelper active/inactive flags. */
export const DYNAMIC_CONTENT_ACTIVE_STATUS = 1;
export const DYNAMIC_CONTENT_INACTIVE_STATUS = 0;

export const buildDeactivatePayload = (id) => ({
  id: Number(id),
  status: DYNAMIC_CONTENT_INACTIVE_STATUS,
});

export const canEditDynamicContent = (record) => {
  const flag = record?.can_edit_content;
  if (flag === true || flag === 1 || flag === '1') return true;
  if (typeof flag === 'string' && flag.toLowerCase() === 'true') return true;
  return false;
};

export const parseDisplayDate = (apiDate) => {
  if (!apiDate) return null;
  const m = moment(apiDate, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']);
  return m.isValid() ? m.format('DD-MM-YYYY') : null;
};

export const formatApiDate = (displayDate, endOfDay = false) => {
  if (!displayDate) return null;
  const m = moment(displayDate, 'DD-MM-YYYY');
  if (!m.isValid()) return null;
  return endOfDay ? m.format('YYYY-MM-DD 23:59:59') : m.format('YYYY-MM-DD 00:00:00');
};

export const displayDateToMoment = (displayDate) => {
  if (!displayDate) return null;
  const m = moment(displayDate, 'DD-MM-YYYY', true);
  return m.isValid() ? m : null;
};

export const displayDateToJsDate = (displayDate) => {
  const m = displayDateToMoment(displayDate);
  return m ? m.toDate() : null;
};

export const isEndBeforeStart = (startDisplay, endDisplay) => {
  const start = displayDateToMoment(startDisplay);
  const end = displayDateToMoment(endDisplay);
  if (!start || !end) return false;
  return end.isBefore(start, 'day');
};

/** True when display date (DD-MM-YYYY) is strictly before today's calendar day (local). */
export const isDisplayDateBeforeToday = (displayDate) => {
  const m = displayDateToMoment(displayDate);
  if (!m) return false;
  return m.isBefore(moment().startOf('day'), 'day');
};

export const emptyImageItem = () => ({
  image_url: '',
  redirect_url: '',
});

export const emptyFormState = {
  content_type: 'BANNER',
  title: '',
  target_type: 'project',
  rule_type: 'all',
  target_ids_text: '',
  image_items: [emptyImageItem()],
  status: 1,
  start_at_display: null,
  end_at_display: null,
};

export const normalizeImageItemsFromContent = (contentPayload = {}) => {
  if (Array.isArray(contentPayload.images) && contentPayload.images.length > 0) {
    return contentPayload.images.map((item) => {
      if (typeof item === 'string') {
        return { image_url: item.trim(), redirect_url: '' };
      }
      return {
        image_url: item?.image_url?.trim() || '',
        redirect_url: item?.redirect_url?.trim() || '',
      };
    });
  }

  if (contentPayload.image_url && String(contentPayload.image_url).trim()) {
    return [
      {
        image_url: contentPayload.image_url.trim(),
        redirect_url: contentPayload.redirect_url?.trim() || '',
      },
    ];
  }

  return [emptyImageItem()];
};

export const mapContentToForm = (content) => {
  const contentPayload = content?.content || {};
  const targetIds = (content?.targets || [])
    .map((t) => t.target_id)
    .filter(Boolean);

  const imageItems = normalizeImageItemsFromContent(contentPayload);
  const withUploaded = imageItems.filter((item) => item.image_url);

  return {
    content_type: 'BANNER',
    title: content?.title || '',
    target_type: 'project',
    rule_type: content?.rule_type || 'all',
    target_ids_text: targetIds.join(', '),
    image_items: withUploaded.length ? imageItems : [emptyImageItem()],
    status: content?.status ?? 1,
    start_at_display: parseDisplayDate(content?.start_at),
    end_at_display: parseDisplayDate(content?.end_at),
  };
};

/** Empty is allowed (optional). If provided, must start with http:// or https:// */
export const isValidRedirectUrl = (url) => {
  const trimmed = (url != null ? String(url) : '').trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  return lower.startsWith('http://') || lower.startsWith('https://');
};

/** Max project IDs per content (CSV + manual). Align with backend if needed. */
export const MAX_DYNAMIC_CONTENT_PROJECT_IDS = 20000;

export const parseTargetIds = (text) => {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/[\s,]+/)
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !Number.isNaN(id) && id > 0);
};

export const formatTargetIdsText = (ids) => (ids?.length ? ids.join(', ') : '');

export const assertProjectIdCountWithinLimit = (ids) => {
  if (ids.length > MAX_DYNAMIC_CONTENT_PROJECT_IDS) {
    throw new Error(
      localization.dynamicContentProjectIdLimit.replace(
        '%s',
        String(MAX_DYNAMIC_CONTENT_PROJECT_IDS)
      )
    );
  }
};

/** Parse a single CSV row, respecting quoted fields. */
export const parseCsvLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim().replace(/^"|"$/g, ''));
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim().replace(/^"|"$/g, ''));
  return result;
};

export const parseProjectIdsFromCsvText = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row.');
  }

  const header = parseCsvLine(lines[0]).map((cell) => cell.toLowerCase().trim());
  const projectIdIndex = header.indexOf('project_id');

  if (projectIdIndex === -1) {
    throw new Error(localization.dynamicContentCsvProjectIdColumnRequired);
  }

  const ids = [];
  const seen = new Set();

  for (let i = 1; i < lines.length; i += 1) {
    const columns = parseCsvLine(lines[i]);
    const raw = columns[projectIdIndex];
    if (raw == null || !String(raw).trim()) continue;

    const id = parseInt(String(raw).trim(), 10);
    if (!Number.isNaN(id) && id > 0 && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }

  if (ids.length === 0) {
    throw new Error(localization.dynamicContentCsvNoIds);
  }

  assertProjectIdCountWithinLimit(ids);

  return ids;
};

export const readProjectIdsFromCsvFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file selected.'));
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      reject(new Error('Please upload a .csv file.'));
      return;
    }

    const maxCsvBytes = 5 * 1024 * 1024;
    if (file.size > maxCsvBytes) {
      reject(new Error('CSV file is too large. Maximum size is 5 MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        resolve(parseProjectIdsFromCsvText(event.target.result));
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read CSV file.'));
    reader.readAsText(file);
  });

export const buildSubmitPayload = (formData, options = {}) => {
  const { isEdit = false, originalStartAtDisplay = null, contentId = null } = options;
  const ruleType = formData.rule_type;
  const title = (formData.title != null ? String(formData.title) : '').trim();
  if (!title) {
    return { error: localization.dynamicContentTitleRequired };
  }

  const targetIds = parseTargetIds(formData.target_ids_text);

  try {
    assertProjectIdCountWithinLimit(targetIds);
  } catch (err) {
    return { error: err.message };
  }

  if (['include', 'exclude'].includes(ruleType) && targetIds.length === 0) {
    return { error: 'IDs are required for include and exclude rules.' };
  }

  if (ruleType === 'all' && targetIds.length > 0) {
    return { error: 'IDs must be empty when rule type is All.' };
  }

  const imageItems = (formData.image_items || [])
    .map((item) => ({
      image_url: (item?.image_url != null ? String(item.image_url) : '').trim(),
      redirect_url: (item?.redirect_url != null ? String(item.redirect_url) : '').trim(),
    }))
    .filter((item) => item.image_url);

  if (imageItems.length === 0) {
    return { error: 'Please upload at least one image (JPG, JPEG, PNG).' };
  }

  const invalidRedirectIndex = imageItems.findIndex(
    (item) => !isValidRedirectUrl(item.redirect_url)
  );
  if (invalidRedirectIndex !== -1) {
    return {
      error:
        imageItems.length > 1
          ? `${localization.dynamicContentRedirectUrlInvalid} (image ${invalidRedirectIndex + 1})`
          : localization.dynamicContentRedirectUrlInvalid,
    };
  }

  if (!formData.start_at_display) {
    return { error: 'Start date is required.' };
  }

  const startChanged =
    isEdit &&
    originalStartAtDisplay != null &&
    formData.start_at_display !== originalStartAtDisplay;

  if (isDisplayDateBeforeToday(formData.start_at_display)) {
    if (!isEdit || startChanged) {
      return { error: localization.dynamicContentStartDatePast };
    }
  }

  if (!formData.end_at_display) {
    return { error: 'End date is required.' };
  }

  if (isEndBeforeStart(formData.start_at_display, formData.end_at_display)) {
    return { error: 'End date cannot be before start date.' };
  }

  const contentImages = imageItems.map(({ image_url, redirect_url }) => ({
    image_url,
    ...(redirect_url ? { redirect_url } : {}),
  }));

  const payload = {
    content_type: 'BANNER',
    title,
    target_type: 'project',
    rule_type: ruleType,
    content: {
      images: contentImages,
    },
    priority: 0,
    status: isEdit
      ? DYNAMIC_CONTENT_ACTIVE_STATUS
      : parseInt(formData.status, 10) || DYNAMIC_CONTENT_ACTIVE_STATUS,
    start_at: formatApiDate(formData.start_at_display),
    end_at: formatApiDate(formData.end_at_display, true),
    target_ids: ruleType === 'all' ? [] : targetIds,
  };

  if (isEdit) {
    const id = parseInt(contentId, 10);
    if (Number.isNaN(id) || id < 1) {
      return { error: 'Invalid content id.' };
    }
    payload.id = id;
  }

  return { payload };
};
